import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  createProject,
  getAttachableAgents,
  projectAttachAgent,
  type ProjectRow,
} from '../transport/api'
import { ApiError } from '../transport/http'
import { PickerBody } from '../ui/PickerSheet'
import { machineNameOf } from '../agents/machineName'

// Split out of StartAgentSheet.tsx in M6 (拆面板, 2026-09-03), moved verbatim —
// the sheet's four blocks were already four functions sharing nothing but the file.
//
// 2026-09-09: the attach list is the SAME list Project Chat's "Attach agents" sheet
// draws (PickerBody) — one column, the machine at the right of each row, a search
// box on top, chips of the ticked (owner: 把 + Project 下面列表 Agent 的方式改為這種
// 單 Column, 並且後面顯示 server / machine … 與 Project Chat 裡面 "Attach Agent" 的
// 方式一樣). The chip grid it replaces showed the name and nothing else, so two
// agents with one name on two machines were the same chip twice.
export function CreateProjectSection({
  onCreated,
  onStatus,
}: {
  onCreated: (p: ProjectRow) => void
  onStatus: (msg: string) => void
}) {
  const [projName, setProjName] = useState('')
  /** Channel hashes ticked for the new room. */
  const [attach, setAttach] = useState<string[]>([])

  // Mounted only when the Project tab is looked at, so the vault-gated list is
  // never fetched for the far-more-common agent path.
  const attachable = useQuery({
    queryKey: ['attachable-agents'],
    queryFn: () => getAttachableAgents(),
  })

  const makeProject = useMutation({
    mutationFn: async (n: string) => {
      const r = await createProject(n)
      if (!r.project) return { r, failed: [] as string[] }
      /*
       * Attach after the room exists, and report what did not stick. The project is
       * created either way, so a failed attachment cannot be treated as a failed
       * creation — but it must not be silent either: the user ticked a name and
       * would otherwise open an empty room and believe they had chosen nothing.
       */
      const failed: string[] = []
      for (const hash of attach) {
        const a = attachable.data?.agents.find((x) => x.channel_hash === hash)
        try {
          // device_id MUST travel (avg-main, 2026-08-19): this call site omitted
          // it, the api layer padded with '', and the deviceless AGENT# row that
          // landed in DynamoDB 400'd the feed Lambda — which, throwing on an
          // ordered stream, wedged EVERY agent in the conversation. The attach
          // sheet's sibling call passed it all along; this one just never did.
          const res = await projectAttachAgent(
            r.project.project_id,
            hash,
            a?.label ?? hash,
            a?.device_id,
          )
          if (res.error) failed.push(a?.label ?? hash)
        } catch {
          failed.push(a?.label ?? hash)
        }
      }
      return { r, failed }
    },
    onSuccess: ({ r, failed }) => {
      if (!r.project) {
        onStatus(r.error ?? 'Could not create the project')
        return
      }
      onCreated(r.project)
      if (failed.length) onStatus(`Project created, but couldn’t attach: ${failed.join(', ')}`)
    },
    onError: (e: unknown) =>
      onStatus(e instanceof ApiError ? e.message : 'Could not create the project'),
  })

  return (
    <>
      <div className="nas-sec">Project name</div>
      <input
        className="nas-input"
        value={projName}
        onChange={(e) => setProjName(e.target.value)}
        placeholder="repo-status"
        autoFocus
        onKeyDown={(e) => {
          if (e.key === 'Enter' && projName.trim() && !makeProject.isPending) {
            makeProject.mutate(projName.trim())
          }
        }}
      />
      <div className="nas-sec">
        Attach agents{' '}
        <span className="nas-sub">
          {attachable.isPending
            ? 'loading…'
            : !attachable.data?.agents.length
              ? 'optional — can be changed later'
              : attach.length
                ? `${attach.length} of ${attachable.data.agents.length} selected`
                : `${attachable.data.agents.length} available · optional`}
        </span>
      </div>
      {attachable.isError && (
        <div className="nas-note">
          Couldn’t load the agent list. The project can still be created, and agents attached from
          its room later.
        </div>
      )}
      {!attachable.isError && (
        <div className="nas-attach-list">
          <PickerBody
            placeholder="Search agents…"
            items={(attachable.data?.agents ?? []).map((a) => ({
              id: a.channel_hash,
              label: a.label,
              // The machine, by the name the account gave it — the same rule and the
              // same fallbacks as every other agent row (machineName.ts).
              sub: machineNameOf(a),
              pet: a.label,
            }))}
            emptyText={attachable.isPending ? 'looking for agents…' : 'No agents available to attach.'}
            selected={new Set(attach)}
            onToggle={(hash) =>
              setAttach((v) => (v.includes(hash) ? v.filter((h) => h !== hash) : [...v, hash]))
            }
          />
        </div>
      )}
      {/* Pinned to the sheet's bottom edge, never under the list or the keyboard (owner,
          2026-10-28: 當鍵盤高度過高的時候, 那個 create button 也會被擋住) — .sheet-stick. */}
      <div className="sheet-stick">
        <button
          type="button"
          className="sheet-btn primary"
          disabled={!projName.trim() || makeProject.isPending}
          onClick={() => makeProject.mutate(projName.trim())}
        >
          {makeProject.isPending ? 'Creating…' : 'Create project'}
        </button>
      </div>
    </>
  )
}

/**
 * The machine, as a dropdown.
 *
 * It carries the five states `MachineList` keeps apart, because collapsing them is a
 * bug this codebase has already paid for: "you have paired nothing" and "the BFF did
 * not answer" need different actions from the reader, and an empty dropdown says
 * neither. Only the `servers` case becomes a select; the rest say what happened.
 */
